import React, { useState, useRef, useCallback, useEffect } from 'react';
import { AutoComplete } from 'antd';
import { lookupApi, LookupResult } from '../services/lookupApi';

interface LookupInputProps {
  lookupType: string;
  value: string;
  onChange: (code: string) => void;
  onCodeConfirm: (code: string) => void;
  contractFieldId?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  maxLength?: number;
}

const LookupInput: React.FC<LookupInputProps> = ({
  lookupType, value, onChange, onCodeConfirm, contractFieldId, disabled, style, maxLength,
}) => {
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const confirmedRef = useRef(false);
  // Guard: AntD fires onChange right after onSelect; skip that echo.
  const justSelectedRef = useRef(false);

  const fetchOptions = useCallback(async (keyword?: string) => {
    try {
      const res = await lookupApi.search(lookupType, keyword, 1, 20);
      setOptions(
        (res.data || []).map((item: LookupResult) => ({
          value: item.code,
          label: `${item.code.replace(/\|/g, '')} - ${item.name}`,
        })),
      );
    } catch {
      setOptions([]);
    }
  }, [lookupType]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleSearch = useCallback((text: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchOptions(text), 300);
  }, [fetchOptions]);

  const handleFocus = useCallback(() => {
    confirmedRef.current = false;
    fetchOptions(value || undefined);
  }, [fetchOptions, value]);

  const handleSelect = useCallback((code: string) => {
    justSelectedRef.current = true;
    confirmedRef.current = true;
    onChange(code);
    onCodeConfirm(code);
    setOpen(false);
  }, [onChange, onCodeConfirm]);

  const handleChange = useCallback((text: string) => {
    // AntD fires onChange right after onSelect with the same value — skip it
    // to avoid resetting confirmedRef and double-firing onCodeConfirm on blur.
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    confirmedRef.current = false;
    const trimmed = maxLength ? text.slice(0, maxLength) : text;
    onChange(trimmed);
  }, [onChange, maxLength]);

  const handleBlur = useCallback(() => {
    if (!confirmedRef.current) {
      onCodeConfirm(value);
    }
  }, [onCodeConfirm, value]);

  return (
    <AutoComplete
      value={value}
      options={options}
      open={open}
      onDropdownVisibleChange={setOpen}
      onSearch={handleSearch}
      onFocus={handleFocus}
      onSelect={handleSelect}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      style={style}
      data-contract-field={contractFieldId}
      popupMatchSelectWidth={240}
    />
  );
};

export default React.memo(LookupInput);
